import { useState, useMemo } from 'react'
import {
  Card, Table, Button, Space, Typography, Tag, Row, Col,
  Select, DatePicker, Input, Statistic, message,
} from 'antd'
import { DownloadOutlined, SearchOutlined, DollarOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Text } = Typography

type Bracket = '0-30' | '31-60' | '61-90' | '91-120' | '120+'

interface CreditorAgingRow {
  supplier_name: string
  grn_no: string
  grn_date: string
  po_value: number
  days_outstanding: number
  bracket: Bracket
}

const BRACKET_COLOR: Record<Bracket, string> = {
  '0-30': 'green',
  '31-60': 'blue',
  '61-90': 'gold',
  '91-120': 'orange',
  '120+': 'red',
}

const BRACKETS: Bracket[] = ['0-30', '31-60', '61-90', '91-120', '120+']

const BRACKET_LABELS: Record<Bracket, string> = {
  '0-30': 'Current',
  '31-60': '31-60 Days',
  '61-90': '61-90 Days',
  '91-120': '91-120 Days',
  '120+': '120+ Days',
}

function fmtINR(v: number | undefined | null) {
  if (v == null) return '—'
  return `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export default function CreditorAgingPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [asOfDate, setAsOfDate] = useState<dayjs.Dayjs>(dayjs())
  const [supplierSearch, setSupplierSearch] = useState('')

  const { data: rows = [], isLoading } = useQuery<CreditorAgingRow[]>({
    queryKey: ['creditor-aging', plantId, asOfDate?.format('YYYY-MM-DD')],
    queryFn: () =>
      apiClient
        .get('/mis/creditor-aging', {
          params: {
            plant_id: plantId,
            as_of_date: asOfDate?.format('YYYY-MM-DD'),
          },
        })
        .then((r) => r.data),
    enabled: !!plantId,
  })

  const filtered = useMemo(() => {
    if (!supplierSearch.trim()) return rows
    const q = supplierSearch.toLowerCase()
    return rows.filter((r) => r.supplier_name.toLowerCase().includes(q))
  }, [rows, supplierSearch])

  const bracketSummary = useMemo(() => {
    const acc: Record<Bracket, { amount: number; count: number }> = {
      '0-30': { amount: 0, count: 0 },
      '31-60': { amount: 0, count: 0 },
      '61-90': { amount: 0, count: 0 },
      '91-120': { amount: 0, count: 0 },
      '120+': { amount: 0, count: 0 },
    }
    rows.forEach((r) => {
      if (acc[r.bracket]) {
        acc[r.bracket].amount += r.po_value
        acc[r.bracket].count += 1
      }
    })
    return acc
  }, [rows])

  const totalOutstanding = useMemo(
    () => rows.reduce((s, r) => s + r.po_value, 0),
    [rows],
  )

  const columns = [
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      ellipsis: true,
      sorter: (a: CreditorAgingRow, b: CreditorAgingRow) =>
        a.supplier_name.localeCompare(b.supplier_name),
    },
    {
      title: 'GRN No',
      dataIndex: 'grn_no',
      key: 'grn_no',
      width: 140,
    },
    {
      title: 'GRN Date',
      dataIndex: 'grn_date',
      key: 'grn_date',
      width: 120,
      render: (v: string) => (v ? dayjs(v).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: 'PO Value',
      dataIndex: 'po_value',
      key: 'po_value',
      width: 150,
      align: 'right' as const,
      sorter: (a: CreditorAgingRow, b: CreditorAgingRow) => a.po_value - b.po_value,
      render: (v: number) => <span style={{ fontWeight: 500 }}>{fmtINR(v)}</span>,
    },
    {
      title: 'Days Outstanding',
      dataIndex: 'days_outstanding',
      key: 'days_outstanding',
      width: 140,
      align: 'right' as const,
      sorter: (a: CreditorAgingRow, b: CreditorAgingRow) =>
        a.days_outstanding - b.days_outstanding,
    },
    {
      title: 'Bracket',
      dataIndex: 'bracket',
      key: 'bracket',
      width: 110,
      filters: BRACKETS.map((b) => ({ text: BRACKET_LABELS[b], value: b })),
      onFilter: (value: any, record: CreditorAgingRow) => record.bracket === value,
      render: (v: Bracket) => <Tag color={BRACKET_COLOR[v]}>{v}</Tag>,
    },
  ]

  const cardColors: Record<Bracket, string> = {
    '0-30': '#52c41a',
    '31-60': '#1677ff',
    '61-90': '#faad14',
    '91-120': '#fa8c16',
    '120+': '#ff4d4f',
  }

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Creditor Aging Report"
        subtitle="Accounts Payable — outstanding GRNs by aging bracket"
        extra={
          <Button
            icon={<DownloadOutlined />}
            onClick={() => message.info('Export to Excel coming soon')}
          >
            Export
          </Button>
        }
      />

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            value={plantId}
            onChange={setPlantId}
            style={{ width: 180 }}
            placeholder="Select Plant"
            options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
          />
          <DatePicker
            value={asOfDate}
            onChange={(v) => v && setAsOfDate(v)}
            format="DD-MMM-YYYY"
            allowClear={false}
          />
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search supplier..."
            value={supplierSearch}
            onChange={(e) => setSupplierSearch(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
        </Space>
      </Card>

      {/* Summary Cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {BRACKETS.map((b) => (
          <Col key={b} xs={24} sm={12} md={8} lg={6} xl={4} style={{ flex: '1 1 0' }}>
            <Card
              size="small"
              style={{ borderTop: `3px solid ${cardColors[b]}` }}
            >
              <Statistic
                title={
                  <span>
                    <Tag color={BRACKET_COLOR[b]} style={{ marginRight: 4 }}>{b}</Tag>
                    {BRACKET_LABELS[b]}
                  </span>
                }
                value={bracketSummary[b].amount}
                precision={2}
                prefix="₹"
                valueStyle={{ color: cardColors[b], fontSize: 16 }}
                formatter={(v) =>
                  Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })
                }
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {bracketSummary[b].count} GRN{bracketSummary[b].count !== 1 ? 's' : ''}
              </Text>
            </Card>
          </Col>
        ))}
        <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ flex: '1 1 0' }}>
          <Card
            size="small"
            style={{ borderTop: '3px solid #722ed1', background: '#f9f0ff' }}
          >
            <Statistic
              title={
                <Space>
                  <DollarOutlined />
                  <span>Total Outstanding</span>
                </Space>
              }
              value={totalOutstanding}
              precision={2}
              prefix="₹"
              valueStyle={{ color: '#722ed1', fontSize: 16, fontWeight: 700 }}
              formatter={(v) =>
                Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })
              }
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {rows.length} total GRN{rows.length !== 1 ? 's' : ''}
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Detail Table */}
      <Card>
        <Table
          dataSource={filtered.map((r, i) => ({ ...r, key: i }))}
          columns={columns}
          loading={isLoading}
          size="small"
          scroll={{ x: 900 }}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} records` }}
          onRow={(r: any) => ({
            style:
              r.bracket === '120+'
                ? { background: '#fff1f0' }
                : {},
          })}
          summary={() =>
            filtered.length > 0 ? (
              <Table.Summary.Row style={{ background: '#fafafa' }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong>Total Outstanding</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong style={{ color: '#1677ff' }}>
                    {fmtINR(totalOutstanding)}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
                <Table.Summary.Cell index={3} />
              </Table.Summary.Row>
            ) : null
          }
        />
      </Card>
    </div>
  )
}
